import Backbone from "backbone";
import SequencerNodeUtils from "models/audioEditor/SequencerNodeUtils";
import SequencerNodeFactory from "models/audioEditor/sequencerNodes/SequencerNodeFactory";
import TurboEditorCommentsAPI from "models/audioEditor/TurboEditorCommentsAPI";
import LocalStorageHelper from "models/helper/LocalStorageHelper";

const COMMENT_FILTER = {
    ALL: "all",
    OPEN: "open",
    RESOLVED: "resolved",
    FOR_ME: "forMe",
};

function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "");
}

function normalizeMentionToken(value) {
    return (value || "").replace(/^@/, "").trim().toLowerCase();
}

function uniqueByKey(items, keyGetter) {
    const seen = {};
    return items.filter(function(item) {
        const key = keyGetter(item);
        if (!key || seen[key]) {
            return false;
        }
        seen[key] = true;
        return true;
    });
}

function getMentionKey(mentionCandidate) {
    return mentionCandidate && (mentionCandidate.email || mentionCandidate.username || mentionCandidate.userUID || mentionCandidate.label);
}

function getErrorMessage(error) {
    if (!error) {
        return "Something went wrong. Please try again.";
    }
    return error.errorMessage || error.message || error.statusText || error.toString();
}

function createClientRequestId(prefix) {
    const randomPart = window.ID ? window.ID() : Math.random().toString(36).substring(2);
    return prefix + "_" + Date.now() + "_" + randomPart;
}

function normalizeIdentityValue(value) {
    return value ? value.toString().trim().toLowerCase() : null;
}

const SequencerCommentsWidget = Backbone.Model.extend({
    constructor: function(attributes, options) {
        Backbone.Model.apply(this, [attributes, options]);
        this._sequencer = attributes ? attributes.sequencer : null;
        this._selectionOpenRequestId = 0;
        this.set("commentsPanelOn", false);
        this.set("comments", []);
        this.set("activeCommentId", null);
        this.set("pendingSelection", null);
        this.set("filter", COMMENT_FILTER.OPEN);
        this.set("mentionCandidates", []);
        this.set("currentUser", null);
        this.set("isLoading", false);
        this.set("isSubmitting", false);
        this.set("isPreparingSelection", false);
        this.set("errorMessage", null);
        this._loadedOnce = false;
        this._commentsLoadRequestId = 0;
        this._pendingLoadCommentsPromise = null;
        if (this._sequencer) {
            this.listenTo(this._sequencer, "projectIdChange", this._handleCommentsProjectIdChange);
        }
        this.COMMENT_FILTER = COMMENT_FILTER;
    },

    getSequencer: function() {
        return this._sequencer;
    },

    getProjectIdForComments: function() {
        if (!this._sequencer) {
            return null;
        }
        if (this._sequencer.getProjectId && this._sequencer.getProjectId()) {
            return this._sequencer.getProjectId();
        }
        if (this._sequencer.getLoadedProjectId && this._sequencer.getLoadedProjectId()) {
            return this._sequencer.getLoadedProjectId();
        }
        return this._sequencer.cid;
    },

    isCommentsPanelOn: function() {
        return this.get("commentsPanelOn") === true;
    },

    openCommentsPanel: function() {
        this._selectionOpenRequestId = this._selectionOpenRequestId + 1;
        this.set("commentsPanelOn", true);
        return this.loadComments();
    },

    openCommentsPanelForComment: function(commentId, options) {
        if (!commentId) {
            return Promise.resolve(null);
        }
        this._selectionOpenRequestId = this._selectionOpenRequestId + 1;
        this.set("commentsPanelOn", true);
        this.set("pendingSelection", null);
        return this.loadComments().then((function() {
            const comment = this.getComments().find(function(candidate) {
                return candidate.id === commentId;
            }) || null;
            if (!comment) {
                this.set("activeCommentId", null);
                return null;
            }
            this.set("filter", comment.status === "resolved" ? COMMENT_FILTER.RESOLVED : COMMENT_FILTER.OPEN);
            this.set("focusedMessageId", options && options.messageId ? options.messageId : null);
            this.setActiveCommentId(comment.id, true);
            return comment;
        }).bind(this));
    },

    openCommentsPanelForSelection: function(selectionInfo) {
        this._selectionOpenRequestId = this._selectionOpenRequestId + 1;
        const selectionOpenRequestId = this._selectionOpenRequestId;
        this.set("commentsPanelOn", true);
        this.trigger("commentFocusCleared");
        this.set("isPreparingSelection", true);
        this.set("errorMessage", null);
        const draftSelectionPayload = this._createSelectionPayload(selectionInfo, {});
        if (draftSelectionPayload) {
            this.set("pendingSelection", draftSelectionPayload);
            this.set("activeCommentId", null);
        }
        const anchorPromise = this._sequencer && this._sequencer.ensureSavedForCommentAnchor
            ? this._sequencer.ensureSavedForCommentAnchor(selectionInfo)
            : Promise.resolve({});
        return anchorPromise.then((function(anchorDetails) {
            if (selectionOpenRequestId !== this._selectionOpenRequestId || !this.isCommentsPanelOn()) {
                return null;
            }
            const selectionPayload = this._createSelectionPayload(selectionInfo, anchorDetails);
            if (!selectionPayload) {
                this.set("isPreparingSelection", false);
                return null;
            }
            this.set("pendingSelection", selectionPayload);
            this.set("activeCommentId", null);
            this.set("isPreparingSelection", false);
            return this.loadComments();
        }).bind(this)).catch((function(error) {
            if (selectionOpenRequestId !== this._selectionOpenRequestId || !this.isCommentsPanelOn()) {
                return null;
            }
            this.set("isPreparingSelection", false);
            this.set("pendingSelection", null);
            this.set("activeCommentId", null);
            this.set("errorMessage", getErrorMessage(error));
            throw error;
        }).bind(this));
    },

    closeCommentsPanel: function() {
        this._selectionOpenRequestId = this._selectionOpenRequestId + 1;
        this.set("commentsPanelOn", false);
        this.set("pendingSelection", null);
        this.set("activeCommentId", null);
        this.set("isPreparingSelection", false);
        this.trigger("commentFocusCleared");
    },

    _handleCommentsProjectIdChange: function() {
        return this.loadComments({ force: true }).catch(function(error) {
            console.warn("Failed to reload Turbo Editor comments after project id changed:", error);
        });
    },

    loadComments: function(options) {
        options = options || {};
        const commentsProjectId = this.getProjectIdForComments();
        if (!commentsProjectId) {
            return Promise.resolve(this.get("comments"));
        }
        if (this.get("isLoading") && !options.force) {
            return this._pendingLoadCommentsPromise || Promise.resolve(this.get("comments"));
        }
        const loadRequestId = this._commentsLoadRequestId + 1;
        this._commentsLoadRequestId = loadRequestId;
        this.set("isLoading", true);
        this.set("errorMessage", null);
        this._pendingLoadCommentsPromise = TurboEditorCommentsAPI.getInstance().listComments({
            anchorProjectId: commentsProjectId,
            sequencerId: this._sequencer ? this._sequencer.cid : null,
        }).then((function(response) {
            if (loadRequestId !== this._commentsLoadRequestId || commentsProjectId !== this.getProjectIdForComments()) {
                return this.get("comments");
            }
            this._loadedOnce = true;
            this.set("comments", response.comments || []);
            this.set("mentionCandidates", response.mentionCandidates || []);
            this.set("currentUser", response.currentUser || this.getCurrentUserForComments());
            this.set("isLoading", false);
            this._pendingLoadCommentsPromise = null;
            return this.get("comments");
        }).bind(this)).catch((function(error) {
            if (loadRequestId === this._commentsLoadRequestId) {
                this.set("isLoading", false);
                this.set("errorMessage", getErrorMessage(error));
                this._pendingLoadCommentsPromise = null;
            }
            throw error;
        }).bind(this));
        return this._pendingLoadCommentsPromise;
    },

    getComments: function() {
        return this.get("comments") || [];
    },

    getMentionCandidates: function() {
        return this.get("mentionCandidates") || [];
    },

    getCurrentUserForComments: function() {
        const currentUser = this.get("currentUser");
        if (currentUser && (currentUser.userUID || currentUser.email || currentUser.username)) {
            return currentUser;
        }
        const userInfo = LocalStorageHelper.getInstance().getUserInfo() || {};
        return {
            userUID: userInfo.userUID || userInfo._id || userInfo.id || null,
            email: userInfo.email || null,
            username: userInfo.username || null,
        };
    },

    getCurrentUserMentionIdentity: function() {
        const currentUser = this.getCurrentUserForComments();
        return {
            userUID: normalizeIdentityValue(currentUser.userUID || currentUser._id || currentUser.id),
            email: normalizeIdentityValue(currentUser.email),
            username: normalizeIdentityValue(currentUser.username),
        };
    },

    searchMentionCandidates: function(query) {
        const cleanedQuery = (query || "").trim();
        return TurboEditorCommentsAPI.getInstance().listMentionCandidates({
            anchorProjectId: this.getProjectIdForComments(),
            sequencerId: this._sequencer ? this._sequencer.cid : null,
            query: cleanedQuery,
        }).then((function(response) {
            const mentionCandidates = response.mentionCandidates || [];
            const mergedCandidates = uniqueByKey(
                mentionCandidates.concat(this.getMentionCandidates()),
                getMentionKey
            );
            this.set("mentionCandidates", mergedCandidates);
            return mentionCandidates;
        }).bind(this)).catch((function(error) {
            console.warn("Failed to search Turbo Editor comment mention candidates:", error);
            return this.getMentionCandidates();
        }).bind(this));
    },

    setFilter: function(filter) {
        this.set("filter", filter || COMMENT_FILTER.OPEN);
        this.set("errorMessage", null);
        const activeComment = this.getActiveComment();
        if (!activeComment || this.getFilteredComments().indexOf(activeComment) === -1) {
            const firstComment = this.getFilteredComments()[0] || null;
            this.setActiveCommentId(firstComment ? firstComment.id : null, !!firstComment);
        }
    },

    getFilteredComments: function() {
        const filter = this.get("filter");
        const comments = this.getComments();
        if (filter === COMMENT_FILTER.ALL) {
            return comments;
        }
        if (filter === COMMENT_FILTER.RESOLVED) {
            return comments.filter(function(comment) {
                return comment.status === "resolved";
            });
        }
        if (filter === COMMENT_FILTER.FOR_ME) {
            const currentUserIdentity = this.getCurrentUserMentionIdentity();
            const currentEmail = currentUserIdentity.email;
            const currentUsername = currentUserIdentity.username;
            const currentUserUID = currentUserIdentity.userUID;
            if (!currentEmail && !currentUsername && !currentUserUID) {
                return [];
            }
            return comments.filter(function(comment) {
                const messages = comment.messages || [];
                return messages.some(function(message) {
                    return (message.mentions || []).some(function(mention) {
                        const mentionEmail = normalizeIdentityValue(mention.email);
                        const mentionUsername = normalizeIdentityValue(mention.username);
                        const mentionUserUID = normalizeIdentityValue(mention.userUID || mention._id || mention.id);
                        return (currentEmail && mentionEmail === currentEmail)
                            || (currentUsername && mentionUsername === currentUsername)
                            || (currentUserUID && mentionUserUID === currentUserUID);
                    });
                });
            });
        }
        return comments.filter(function(comment) {
            return comment.status !== "resolved";
        });
    },

    getActiveComment: function() {
        const activeCommentId = this.get("activeCommentId");
        return this.getComments().find(function(comment) {
            return comment.id === activeCommentId;
        }) || null;
    },

    getActiveCommentIndex: function() {
        const activeCommentId = this.get("activeCommentId");
        return this.getFilteredComments().findIndex(function(comment) {
            return comment.id === activeCommentId;
        });
    },

    canMoveToPreviousComment: function() {
        return this.getActiveCommentIndex() > 0;
    },

    canMoveToNextComment: function() {
        const index = this.getActiveCommentIndex();
        return index >= 0 && index < this.getFilteredComments().length - 1;
    },

    setActiveCommentId: function(commentId, focusComment) {
        if (commentId) {
            this.set("pendingSelection", null);
        }
        this.set("activeCommentId", commentId || null);
        const comment = this.getActiveComment();
        if (focusComment !== false && comment) {
            this.trigger("commentFocusRequested", comment);
        }
        if (!comment) {
            this.trigger("commentFocusCleared");
        }
        return comment;
    },

    moveToNextComment: function() {
        const comments = this.getFilteredComments();
        if (!comments.length) {
            return null;
        }
        const currentIndex = this.getActiveCommentIndex();
        const nextIndex = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, comments.length - 1);
        return this.setActiveCommentId(comments[nextIndex].id, true);
    },

    moveToPreviousComment: function() {
        const comments = this.getFilteredComments();
        if (!comments.length) {
            return null;
        }
        const currentIndex = this.getActiveCommentIndex();
        const previousIndex = currentIndex < 0 ? 0 : Math.max(currentIndex - 1, 0);
        return this.setActiveCommentId(comments[previousIndex].id, true);
    },

    submitPendingComment: function(body) {
        const cleanedBody = (body || "").trim();
        const pendingSelection = this.get("pendingSelection");
        if (!cleanedBody || !pendingSelection) {
            return Promise.resolve(null);
        }
        this.set("isSubmitting", true);
        this.set("errorMessage", null);
        return TurboEditorCommentsAPI.getInstance().createComment({
            anchorProjectId: this.getProjectIdForComments(),
            sequencerId: this._sequencer ? this._sequencer.cid : null,
            selectionAnchor: pendingSelection,
            selection: pendingSelection,
            body: cleanedBody,
            mentions: this.extractMentions(cleanedBody),
            clientRequestId: createClientRequestId("turbo_comment"),
        }).then((function(response) {
            const comment = response.comment;
            this.set("pendingSelection", null);
            this.set("comments", [comment].concat(this.getComments()));
            this.set("isSubmitting", false);
            this.setActiveCommentId(comment.id, true);
            return comment;
        }).bind(this)).catch((function(error) {
            this.set("isSubmitting", false);
            this.set("errorMessage", getErrorMessage(error));
            throw error;
        }).bind(this));
    },

    replyToComment: function(commentId, body) {
        const cleanedBody = (body || "").trim();
        if (!cleanedBody || !commentId) {
            return Promise.resolve(null);
        }
        this.set("isSubmitting", true);
        this.set("errorMessage", null);
        return TurboEditorCommentsAPI.getInstance().createReply({
            anchorProjectId: this.getProjectIdForComments(),
            sequencerId: this._sequencer ? this._sequencer.cid : null,
            commentId: commentId,
            body: cleanedBody,
            mentions: this.extractMentions(cleanedBody),
            clientRequestId: createClientRequestId("turbo_comment_reply"),
        }).then((function(response) {
            this._replaceComment(response.comment);
            this.set("isSubmitting", false);
            this.setActiveCommentId(response.comment.id, false);
            return response.comment;
        }).bind(this)).catch((function(error) {
            this.set("isSubmitting", false);
            this.set("errorMessage", getErrorMessage(error));
            throw error;
        }).bind(this));
    },

    resolveComment: function(commentId) {
        return this._updateCommentStatus(commentId, "resolved");
    },

    reopenComment: function(commentId) {
        return this._updateCommentStatus(commentId, "open");
    },

    _updateCommentStatus: function(commentId, status) {
        this.set("errorMessage", null);
        return TurboEditorCommentsAPI.getInstance().updateComment({
            anchorProjectId: this.getProjectIdForComments(),
            sequencerId: this._sequencer ? this._sequencer.cid : null,
            commentId: commentId,
            status: status,
        }).then((function(response) {
            this._replaceComment(response.comment);
            this.setActiveCommentId(response.comment.id, false);
            return response.comment;
        }).bind(this)).catch((function(error) {
            this.set("errorMessage", getErrorMessage(error));
            throw error;
        }).bind(this));
    },

    _replaceComment: function(updatedComment) {
        const comments = this.getComments().map(function(comment) {
            return comment.id === updatedComment.id ? updatedComment : comment;
        });
        this.set("comments", comments);
    },

    extractMentions: function(body) {
        const tokens = body.match(/@([a-zA-Z0-9_.+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+|[a-zA-Z0-9_.-]+)/g) || [];
        const mentionCandidates = this.getMentionCandidates();
        const mentions = tokens.map(function(token) {
            const normalizedToken = normalizeMentionToken(token);
            const matchedMember = mentionCandidates.find(function(candidate) {
                const email = candidate.email ? candidate.email.toLowerCase() : null;
                const username = candidate.username ? candidate.username.toLowerCase() : null;
                return email === normalizedToken || username === normalizedToken;
            });
            if (matchedMember) {
                return {
                    type: "workspaceMember",
                    userUID: matchedMember.userUID,
                    name: matchedMember.name,
                    username: matchedMember.username,
                    email: matchedMember.email,
                    label: matchedMember.name || matchedMember.email || matchedMember.username,
                };
            }
            if (isValidEmail(normalizedToken)) {
                return {
                    type: "emailReviewerInvite",
                    email: normalizedToken,
                    label: normalizedToken,
                    inviteStatus: "pending",
                };
            }
            return null;
        }).filter(Boolean);
        return uniqueByKey(mentions, function(mention) {
            return mention.email || mention.username || mention.userUID;
        });
    },

    getSequencerNodesForComment: function(comment) {
        const selectionAnchor = comment ? (comment.selectionAnchor || comment.selection) : null;
        if (!comment || !selectionAnchor || !this._sequencer || !this._sequencer.getSequencerNodeByNodeCid) {
            return [];
        }
        const anchorProjectId = selectionAnchor.anchorProjectId || comment.anchorProjectId || comment.projectId || this.getProjectIdForComments();
        const isUsableNode = function(node) {
            return (!node.isAddedToSequencer || node.isAddedToSequencer())
                && (!node.getSelectable || node.getSelectable());
        };
        const resolveSavedNode = (function(nodeCid) {
            return nodeCid ? SequencerNodeFactory.getInstance().getSequencerNodeUsingNodeCidFromProject(nodeCid, anchorProjectId, this._sequencer) : null;
        }).bind(this);
        const resolveLiveNode = (function(nodeCid) {
            return nodeCid ? this._sequencer.getSequencerNodeByNodeCid(nodeCid) : null;
        }).bind(this);
        const getNodesFromCids = function(nodeCids, resolver) {
            const cids = nodeCids || [];
            if (!cids.length) {
                return [];
            }
            const nodes = cids.map(resolver);
            if (nodes.some(function(node) { return !node; })) {
                return [];
            }
            return nodes.filter(isUsableNode);
        };
        const savedSelectedNodes = getNodesFromCids(selectionAnchor.savedSelectedNodeCids, resolveSavedNode);
        if (savedSelectedNodes.length) {
            return savedSelectedNodes;
        }

        const savedStartNodeCid = selectionAnchor.savedStartNodeCid || selectionAnchor.startNodeCid;
        const savedEndNodeCid = selectionAnchor.savedEndNodeCid || selectionAnchor.endNodeCid;
        const startNode = resolveSavedNode(savedStartNodeCid);
        const endNode = resolveSavedNode(savedEndNodeCid);
        if (startNode && endNode) {
            const nodes = SequencerNodeUtils.getInstance().getArrayOfSequencerNodesFromStartToEnd(startNode, endNode);
            if (nodes.length && nodes[nodes.length - 1] === endNode) {
                return nodes.filter(isUsableNode);
            }
        }

        const liveSelectedNodes = getNodesFromCids(selectionAnchor.liveSelectedNodeCids, resolveLiveNode);
        if (liveSelectedNodes.length) {
            return liveSelectedNodes;
        }

        const liveStartNode = resolveLiveNode(selectionAnchor.liveStartNodeCid || selectionAnchor.startNodeCid);
        const liveEndNode = resolveLiveNode(selectionAnchor.liveEndNodeCid || selectionAnchor.endNodeCid);
        if (!liveStartNode || !liveEndNode) {
            return [];
        }
        const liveRangeNodes = SequencerNodeUtils.getInstance().getArrayOfSequencerNodesFromStartToEnd(liveStartNode, liveEndNode);
        if (!liveRangeNodes.length || liveRangeNodes[liveRangeNodes.length - 1] !== liveEndNode) {
            return [];
        }
        return liveRangeNodes.filter(isUsableNode);
    },

    _createSelectionPayload: function(selectionInfo, anchorDetails) {
        if (!selectionInfo || !selectionInfo.startSequencerNodeModel || !selectionInfo.endSequencerNodeModel) {
            return null;
        }
        anchorDetails = anchorDetails || {};
        const startNode = selectionInfo.startSequencerNodeModel;
        const endNode = selectionInfo.endSequencerNodeModel;
        const startSegment = startNode.getAudioSegment ? startNode.getAudioSegment() : null;
        const endSegment = endNode.getAudioSegment ? endNode.getAudioSegment() : null;
        const selectedNodes = SequencerNodeUtils.getInstance().getArrayOfSequencerNodesFromStartToEnd(startNode, endNode);
        const liveSelectedNodeCids = anchorDetails.liveSelectedNodeCids || selectedNodes.map(function(node) {
            return node.cid;
        });
        const savedSelectedNodeCids = anchorDetails.savedSelectedNodeCids || liveSelectedNodeCids;
        const selectedText = this._sequencer && this._sequencer.getSelectedNodesTextContent
            ? this._sequencer.getSelectedNodesTextContent(startNode, endNode, false)
            : "";
        return {
            anchorProjectId: anchorDetails.anchorProjectId || this.getProjectIdForComments(),
            snapshotVersionProjectId: anchorDetails.snapshotVersionProjectId || (this._sequencer && this._sequencer.getVersionedProjectId ? this._sequencer.getVersionedProjectId() : null),
            savedStartNodeCid: anchorDetails.savedStartNodeCid || savedSelectedNodeCids[0] || startNode.cid,
            savedEndNodeCid: anchorDetails.savedEndNodeCid || savedSelectedNodeCids[savedSelectedNodeCids.length - 1] || endNode.cid,
            savedSelectedNodeCids: savedSelectedNodeCids,
            liveStartNodeCid: anchorDetails.liveStartNodeCid || startNode.cid,
            liveEndNodeCid: anchorDetails.liveEndNodeCid || endNode.cid,
            liveSelectedNodeCids: liveSelectedNodeCids,
            startNodeCid: anchorDetails.savedStartNodeCid || savedSelectedNodeCids[0] || startNode.cid,
            endNodeCid: anchorDetails.savedEndNodeCid || savedSelectedNodeCids[savedSelectedNodeCids.length - 1] || endNode.cid,
            startSegmentId: startSegment && startSegment.id ? startSegment.id : null,
            endSegmentId: endSegment && endSegment.id ? endSegment.id : null,
            startTime: startSegment && startSegment.getStartTime ? startSegment.getStartTime() : null,
            endTime: endSegment && endSegment.getEndTime ? endSegment.getEndTime() : null,
            selectionText: selectedText,
            text: selectedText,
        };
    },
});

SequencerCommentsWidget.COMMENT_FILTER = COMMENT_FILTER;

export default SequencerCommentsWidget;
